/**
 * Keeps `syncStore` current: restore from IndexedDB, bootstrap when there
 * is nothing to restore or the server says to, then follow the socket
 * (back-end-rs/docs/SYNC_ENGINE.md §6–7).
 */
import { jwtDecode } from 'jwt-decode';
import {
  getRefreshToken,
  getRememberMe,
  getToken,
  refreshAccessToken,
  setToken,
  subscribeToToken,
} from '../utils/TokenUtils';
import { BootstrapPayload, SCHEMA, ServerMessage } from './models';
import * as persist from './persist';
import { SyncStore } from './store';

export type SyncStatus =
  /** Nothing yet. */
  | 'idle'
  /** Showing the copy from the last session; catching up. */
  | 'cached'
  /** Downloading everything. */
  | 'bootstrapping'
  /** Current, and following changes as they happen. */
  | 'live'
  /** Showing what we have; reconnecting. */
  | 'offline'
  /** No copy and no way to get one: views fall back to fetching. */
  | 'error';

/** Where the engine lives: set locally, the API host's /sync in the cluster. */
export const syncBaseUrl = (): string =>
  window._env_.REACT_APP_SYNC_SVC || `${window._env_.REACT_APP_AUTH_SVC}/sync`;

const REFRESH_AHEAD_S = 120;
const MAX_BACKOFF_MS = 30_000;
const PERSIST_DEBOUNCE_MS = 500;

/**
 * The access token, refreshed first if it is within two minutes of expiry
 * — the socket is closed at `exp`, so it is handed a new one before then.
 */
export async function freshToken(): Promise<string | null> {
  let token = getToken();
  if (!token) return null;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    if (exp - Date.now() / 1000 < REFRESH_AHEAD_S) {
      const refresh = getRefreshToken();
      const next = refresh ? await refreshAccessToken(refresh) : null;
      if (next) {
        setToken(next, !!getRememberMe());
        token = next;
      }
    }
  } catch {
    // An undecodable token is the server's to reject.
  }
  return token;
}

export class SyncClient {
  private socket: WebSocket | null = null;
  private stopped = false;
  private backoff = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeToken: (() => void) | null = null;
  private status: SyncStatus = 'idle';
  private statusListeners = new Set<(s: SyncStatus) => void>();

  constructor(
    private readonly store: SyncStore,
    private readonly userId: string,
    private readonly baseUrl: string = syncBaseUrl()
  ) {}

  getStatus = (): SyncStatus => this.status;

  onStatus = (listener: (s: SyncStatus) => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  };

  async start(): Promise<void> {
    // A token refreshed by the API client reaches the open socket too.
    this.unsubscribeToken = subscribeToToken(() => this.sendAuth());

    const saved = await persist.load(this.userId);
    if (this.stopped) return;
    if (saved) {
      this.store.replace(saved);
      this.setStatus('cached');
    } else if (!(await this.bootstrap())) {
      return;
    }
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.unsubscribeToken?.();
    [this.reconnectTimer, this.refreshTimer, this.persistTimer].forEach(
      (t) => t && clearTimeout(t)
    );
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close(1000, 'stopped');
    }
  }

  private setStatus(s: SyncStatus): void {
    this.status = s;
    this.statusListeners.forEach((l) => l(s));
  }

  /** Download everything this user may see. False if that failed. */
  private async bootstrap(): Promise<boolean> {
    this.setStatus('bootstrapping');
    for (let attempt = 0; attempt < 2; attempt++) {
      const token = await freshToken();
      if (!token || this.stopped) return false;
      try {
        const res = await fetch(`${this.baseUrl}/bootstrap`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (res.status === 401 && attempt === 0) continue;
        if (!res.ok) throw new Error(`bootstrap: HTTP ${res.status}`);
        const payload = (await res.json()) as BootstrapPayload;
        if (payload.schema !== SCHEMA) {
          throw new Error(`bootstrap: schema ${payload.schema}, expected ${SCHEMA}`);
        }
        if (this.stopped) return false;
        this.store.replace(payload);
        this.persistSoon();
        return true;
      } catch (e) {
        console.warn('Sync bootstrap failed; views will fetch directly.', e);
        break;
      }
    }
    // Keep a restored copy on screen if there is one; otherwise views fall
    // back to fetching for themselves.
    this.setStatus(this.store.isReady() ? 'offline' : 'error');
    this.scheduleReconnect(true);
    return false;
  }

  private wsUrl(): string {
    return `${this.baseUrl.replace(/^http/, 'ws')}/ws`;
  }

  private connect(): void {
    if (this.stopped) return;
    const socket = new WebSocket(this.wsUrl());
    this.socket = socket;

    socket.onopen = async () => {
      const token = await freshToken();
      if (!token || socket.readyState !== WebSocket.OPEN) return;
      socket.send(
        JSON.stringify({ type: 'auth', token, since: this.store.lastSyncId ?? -1 })
      );
      this.scheduleTokenRefresh(token);
    };

    socket.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }
      switch (msg.type) {
        case 'delta':
          this.store.apply(msg.actions, msg.syncId);
          this.persistSoon();
          break;
        case 'live':
          this.store.advance(msg.lastSyncId);
          this.backoff = 1000;
          this.setStatus('live');
          break;
        case 'resync':
          // The close that follows (4002) does the work.
          break;
        default:
          break;
      }
    };

    socket.onclose = async (event) => {
      if (this.socket === socket) this.socket = null;
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      if (this.stopped) return;
      if (event.code === 4002) {
        // Too far behind, or access changed: start over, then follow.
        if (await this.bootstrap()) this.connect();
        return;
      }
      if (this.store.isReady()) this.setStatus('offline');
      // 4001: the token was refused. freshToken() refreshes it on the way
      // back in; if the session is really over, the API client's own 401
      // handling sends the person to sign in.
      this.scheduleReconnect(false);
    };
  }

  private scheduleReconnect(withBootstrap: boolean): void {
    if (this.stopped || this.reconnectTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (withBootstrap && !(await this.bootstrap())) return;
      this.connect();
    }, delay);
  }

  /** Hand the socket a new token two minutes before this one expires. */
  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      const ms = Math.max(5_000, (exp - REFRESH_AHEAD_S) * 1000 - Date.now());
      this.refreshTimer = setTimeout(() => this.sendAuth(), ms);
    } catch {
      // No expiry to plan for.
    }
  }

  private async sendAuth(): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const token = await freshToken();
    if (!token || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ type: 'auth', token }));
    this.scheduleTokenRefresh(token);
  }

  /** Coalesce writes: a burst of packets is one IndexedDB write. */
  private persistSoon(): void {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const snapshot = this.store.snapshot();
      if (snapshot && !this.stopped) persist.save(this.userId, snapshot);
    }, PERSIST_DEBOUNCE_MS);
  }
}
